import React, { useMemo } from 'react'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import { fareTypeLabelMap } from 'constants/flight'
import Image from 'components/Common/Image'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalBase from 'LuxKit/Modal/ModalBase'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightDealLabel from './FlightDealLabel'
import { formatFlightDealTravelPeriods } from './formatFlightDealTravelPeriods'

interface Props {
  activeDeal: App.FlightDeal
}

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
`

const LabelOverlay = styled.div`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
  z-index: 1;
`

function FlightDealModal(props: Props) {
  const { activeDeal } = props

  const travelPeriodsText = useMemo(() => {
    if (!activeDeal?.travelPeriods) return ''
    return formatFlightDealTravelPeriods(activeDeal.travelPeriods)
  }, [activeDeal?.travelPeriods])

  const formattedSalesEndDate = useMemo(() => {
    if (!activeDeal.salesEndDate) return ''
    return moment(activeDeal.salesEndDate).format('Do MMMM YYYY')
  }, [activeDeal.salesEndDate])

  const formattedPublishDate = useMemo(() => {
    // Use salesStartDate if available, otherwise use current date
    const dateToFormat = activeDeal.salesStartDate || new Date()
    return moment(dateToFormat).format('DD MMM YYYY').toUpperCase()
  }, [activeDeal.salesStartDate])

  return (
    <ModalBase modalId="flight-deal-modal">
      <ModalHeader title={activeDeal.dealName} />
      <ModalBody>
        {activeDeal.heroImage && (
          <ImageContainer>
            <ModalImageThumbnail image={activeDeal.heroImage} imageParams={{ aspectRatio: '3.15' }} />
            <LabelOverlay>
              <FlightDealLabel activeDeal={activeDeal} showCountdown={false} />
            </LabelOverlay>
          </ImageContainer>
        )}
        <ModalContent>
          <VerticalSpacer gap={20}>
            <BodyTextBlock
              startIcon={activeDeal.carrier.logo ? <Image image={activeDeal.carrier.logo} height={24} /> : undefined}
              variant="medium"
            >
              {activeDeal.carrier.name} {activeDeal.originAirportCode}-{activeDeal.destinationAirportCode} {fareTypeLabelMap[activeDeal.fareType]} from{' '}
              <strong><FormatCurrency value={activeDeal.perAdultPrice} format="roundedDollar" />/adult</strong>
            </BodyTextBlock>

            {travelPeriodsText && (
              <VerticalSpacer gap={4}>
                <BodyText variant="large" weight="bold">Travel on select dates between</BodyText>
                <BodyTextBlock startIcon={<LineCalendarIcon />} variant="medium">
                  {travelPeriodsText}
                </BodyTextBlock>
              </VerticalSpacer>
            )}

            <div>
              <Caption variant="small" weight="bold">Terms & Conditions</Caption>
              <Caption variant="small" weight="normal">
                Seats strictly limited, may not be available on all flights. On sale until {formattedSalesEndDate} or unless sold out prior. Surcharges may apply for weekend departure, peak and school holiday travel periods. Fare and tax cost may fluctuate at any time. Flight prices correct at time of publishing - {formattedPublishDate}. Sale price includes checked baggage, excludes service/payment fee (if applicable).
              </Caption>
            </div>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default FlightDealModal
